import { describe_error } from '../../utils/ErrorDescriber';
import React, { useState } from 'react';
import { Modal, Button, Form, Spinner, Alert, ToggleButtonGroup, ToggleButton } from 'react-bootstrap';

import { is_toggle_feature } from './PlanFeaturesTable';

// The two kinds of feature: a usage quota each subscriber gets, or an on/off feature granted by Enabled alone.
const c_KindQuota = 'quota';
const c_KindToggle = 'toggle';

// The period a new usage quota starts with.
const c_DefaultQuotaPeriod = 'month';

// Adds a feature to a plan, or edits one when feature is given. options holds the choices the backend offers for
// the limit fields, the default limit and how an on/off feature is stored. A usage quota has a limit, starting at
// the default; an on/off feature has none to set, and is saved with the stored on/off limits.
// on_save(feature_data) returns a promise and rejects with an Error carrying the message to show.
function FeatureModal({plan, feature, options, on_save, on_close}){
	const is_edit = feature ? true : false;
	const toggle_limits = options.toggle_feature_limits;

	// A quota's period choices: every period but the one that marks an on/off feature.
	const quota_periods = options.limit_periods.filter((period)=>{ return period !== toggle_limits.limit_period; });
	const default_quota_period = quota_periods.includes(c_DefaultQuotaPeriod) ? c_DefaultQuotaPeriod : quota_periods[0];

	const get_initial = (field, fallback)=>{
		if(is_edit && (feature[field] !== null) && (feature[field] !== undefined)){
			return String(feature[field]);
		}

		return fallback;
	};

	const initial_kind = (is_edit && is_toggle_feature(feature, options)) ? c_KindToggle : c_KindQuota;

	const [kind, set_kind] = useState(initial_kind);
	const [name, set_name] = useState(get_initial('name', ''));
	const [code, set_code] = useState(get_initial('code', ''));
	const [description, set_description] = useState(get_initial('description', ''));
	const [is_enabled, set_is_enabled] = useState(is_edit ? feature.is_enabled : true);
	const [sort_order, set_sort_order] = useState(get_initial('sort_order', '0'));
	const [limit_value, set_limit_value] = useState((initial_kind === c_KindQuota) ? get_initial('limit_value', String(options.default_limit_value)) : String(options.default_limit_value));
	const [limit_type, set_limit_type] = useState(get_initial('limit_type', options.limit_types[0]));
	const [limit_period, set_limit_period] = useState((initial_kind === c_KindQuota) ? get_initial('limit_period', default_quota_period) : default_quota_period);
	const [limit_granularity, set_limit_granularity] = useState(get_initial('limit_granularity', ''));
	const [limit_dimension, set_limit_dimension] = useState(get_initial('limit_dimension', options.limit_dimensions[0]));
	const [saving, set_saving] = useState(false);
	const [error, set_error] = useState('');

	// The feature as the backend takes it, or null with the error shown.
	const get_feature_data = ()=>{
		if(name.trim().length === 0){
			set_error('Feature name is required.');
			return null;
		}

		if(!/^\d+$/.test(sort_order.trim())){
			set_error('Sort order must be a whole number of 0 or more.');
			return null;
		}

		const feature_data = {
			plan_id: plan.id,
			name: name.trim(),
			code: code.trim(),
			description: description.trim(),
			is_enabled,
			sort_order: Number(sort_order.trim())
		};

		if(kind === c_KindToggle){
			return Object.assign(feature_data, toggle_limits);
		}

		if((limit_value.trim().length === 0) || !(Number(limit_value.trim()) >= 0)){
			set_error('Limit must be a number of 0 or more.');
			return null;
		}

		return Object.assign(feature_data, {
			limit_value: limit_value.trim(),
			limit_type,
			limit_period,
			limit_granularity: limit_granularity ? limit_granularity : null,
			limit_dimension
		});
	};

	const handle_save = async(event)=>{
		event.preventDefault();

		const feature_data = get_feature_data();
		if(!feature_data){
			return;
		}

		set_error('');
		set_saving(true);

		try {
			await on_save(feature_data);
		}
		catch (error) {
			console.log('FeatureModal/handle_save - ERROR: ', describe_error(error));
			set_error(error.message);
			set_saving(false);
		}
	};

	const render_save_label = ()=>{
		if(saving){
			return (
				<Spinner animation="border" size="sm"/>
			);
		}

		return is_edit ? 'Save' : 'Add Feature';
	};

	// A dropdown of one limit field's choices, with an empty first choice when the field is optional.
	const render_limit_select = (label, value, set_value, choices, optional)=>{
		return (
			<Form.Group className="subscription-modal-field">
				<Form.Label>{label}</Form.Label>
				<Form.Select
					value={value}
					disabled={saving}
					onChange={(event)=>{ set_value(event.target.value); }}>
					{optional && <option value="">None</option>}
					{choices.map((choice)=>{
						return (
							<option key={choice} value={choice}>{choice}</option>
						);
					})}
				</Form.Select>
			</Form.Group>
		);
	};

	// An on/off feature has nothing more to set: a plan grants it when Enabled is ticked. A usage quota has its limit.
	const render_kind_fields = ()=>{
		if(kind === c_KindToggle){
			return (
				<p className="subscription-modal-note">
					An on/off feature has no usage limit. The plan includes it when Enabled is ticked.
				</p>
			);
		}

		return (
			<React.Fragment>
				<p className="subscription-modal-note">
					A raised limit reaches existing subscribers straight away. A lowered one reaches them when their
					period next renews.
				</p>

				<div className="subscription-modal-row">
					<Form.Group className="subscription-modal-field">
						<Form.Label>Limit</Form.Label>
						<Form.Control
							type="text"
							value={limit_value}
							disabled={saving}
							onChange={(event)=>{ set_limit_value(event.target.value); }}/>
					</Form.Group>
					{render_limit_select('Type', limit_type, set_limit_type, options.limit_types, false)}
					{render_limit_select('Per', limit_period, set_limit_period, quota_periods, false)}
				</div>

				<div className="subscription-modal-row">
					{render_limit_select('Granularity', limit_granularity, set_limit_granularity, options.limit_granularities, true)}
					{render_limit_select('Dimension', limit_dimension, set_limit_dimension, options.limit_dimensions, false)}
				</div>
			</React.Fragment>
		);
	};

	return (
		<Modal show={true} onHide={on_close} centered size="lg">
			<Modal.Header closeButton>
				<Modal.Title>{(is_edit ? 'Edit Feature' : 'Add Feature') + ': ' + plan.name}</Modal.Title>
			</Modal.Header>

			<Form onSubmit={handle_save}>
				<Modal.Body>
					{error && <Alert variant="danger">{error}</Alert>}

					<div className="subscription-modal-row">
						<Form.Group className="subscription-modal-field">
							<Form.Label>Name</Form.Label>
							<Form.Control
								type="text"
								value={name}
								disabled={saving}
								onChange={(event)=>{ set_name(event.target.value); }}/>
						</Form.Group>

						<Form.Group className="subscription-modal-field">
							<Form.Label>Code</Form.Label>
							<Form.Control
								type="text"
								value={code}
								placeholder="MONTHLY_CREDITS"
								disabled={saving}
								onChange={(event)=>{ set_code(event.target.value.toUpperCase()); }}/>
						</Form.Group>
					</div>

					<Form.Group className="subscription-modal-field">
						<Form.Label>Description</Form.Label>
						<Form.Control
							as="textarea"
							rows={2}
							value={description}
							disabled={saving}
							onChange={(event)=>{ set_description(event.target.value); }}/>
					</Form.Group>

					<div className="subscription-modal-row">
						<Form.Group className="subscription-modal-field">
							<Form.Label>Sort order</Form.Label>
							<Form.Control
								type="text"
								value={sort_order}
								disabled={saving}
								onChange={(event)=>{ set_sort_order(event.target.value); }}/>
						</Form.Group>

						<Form.Group className="subscription-modal-field subscription-modal-check">
							<Form.Check
								type="checkbox"
								label="Enabled"
								checked={is_enabled}
								disabled={saving}
								onChange={(event)=>{ set_is_enabled(event.target.checked); }}/>
						</Form.Group>
					</div>

					<h6 className="subscription-modal-section-title">Kind</h6>
					<ToggleButtonGroup
						type="radio"
						name="feature-kind"
						className="subscription-modal-toggle"
						value={kind}
						onChange={(value)=>{ set_kind(value); }}>
						<ToggleButton id="feature-kind-quota" variant="outline-primary" value={c_KindQuota} disabled={saving}>
							Usage quota
						</ToggleButton>
						<ToggleButton id="feature-kind-toggle" variant="outline-primary" value={c_KindToggle} disabled={saving}>
							On/off
						</ToggleButton>
					</ToggleButtonGroup>

					{render_kind_fields()}
				</Modal.Body>

				<Modal.Footer>
					<Button variant="secondary" type="button" disabled={saving} onClick={on_close}>
						Cancel
					</Button>
					<Button variant="primary" type="submit" disabled={saving}>
						{render_save_label()}
					</Button>
				</Modal.Footer>
			</Form>
		</Modal>
	);
}

export default FeatureModal;
